'use client'

import { useState, useEffect, memo } from 'react'
import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import {
  MultiSelectProperty,
  DateField,
  PROPERTY_ICONS,
} from '@/ui/property-fields'
import { TopicSubtitleSelector } from '@/capture'
import { useFindOrCreateTopic, useTopics } from '@/topics'
import {
  TaskChecklistSummary,
  EditableTaskChecklist,
  toggleChecklistItem,
} from './checklist'
import { TaskEditForm } from './edit-form'
import { DeleteButton } from '@/ui/delete-button'
import { EditButton } from '@/ui/edit-button'
import { SourceLink } from '@/feed/row-chrome'
import { formatRelativeTime, formatDueDate, formatDateString } from '@/lib/date'
import { format } from 'date-fns'
import { MarkdownContent } from '@/ui/markdown-content'
import { cn } from '@/lib/tailwind'
import {
  ExpandableRow,
  RowActions,
  MetaTagsRow,
  TopicBadges,
} from '@/feed/row-chrome'
// Same read again. Step 2 added this edge; the ticket predicted three edges and did not know about it.
// eslint-disable-next-line import/no-restricted-paths
import { usePeople } from '@/legacy/supabase-store/use-people'
import type { PersonRef } from '@/document'
import type { RowProps } from '../types'
import type { TaskDraft } from './write-spec'
import type { TaskEntry } from './meta'
import { useCompleteTask, useToggleChecklistItem } from './write-spec'
import type { ChecklistItem } from './meta'

interface TaskMetadata {
  status?: string
  priority?: string // Kept for backward compat with existing data
  important?: boolean
  category?: string
  dueDate?: string
  completedAt?: string // ISO timestamp
  checklist?: ChecklistItem[]
}

/**
 * One interface, not seven (#359). Six members left `TaskRowProps`, and most dissolved rather than
 * moved: `onComplete` and `onChecklistToggle` are writes and live in `write-spec.ts`;
 * `isCompleting` went with them; `checklistItems` is derived from the entry;
 * `availablePeople` and `availableTopics` are document reads the row makes itself. `context` and
 * `hideTopics` stay, as `RowProps`' two optional display members.
 */
type TaskRowProps = RowProps<TaskEntry, TaskDraft>

export const TaskRow = memo(
  function TaskRow({
    entry,
    isExpanded,
    onToggle,
    onDelete,
    onSave,
    isDeleting,
    isSaving,
    context,
    hideTopics,
  }: TaskRowProps) {
    const metadata = entry.metadata
    const findOrCreateTopic = useFindOrCreateTopic()
    const { topics: availableTopics } = useTopics('task')
    const { data: availablePeople = [] } = usePeople()
    const completeTask = useCompleteTask()
    const toggleChecklist = useToggleChecklistItem()
    const checklistItems = metadata.checklist ?? null
    const [justCompleted, setJustCompleted] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    // Edit state for title/description (only used in edit mode)
    const [editTitle, setEditTitle] = useState(entry.title)
    const [editDescription, setEditDescription] = useState(entry.content || '')
    const [editImportant, setEditImportant] = useState(
      metadata.important ??
        (metadata.priority === 'urgent' || metadata.priority === 'high'),
    )
    const [editDueDate, setEditDueDate] = useState(metadata.dueDate || '')
    const [editPeopleIds, setEditPeopleIds] = useState<string[]>(
      entry.people?.map((p) => p.id) || [],
    )
    const [editTopicIds, setEditTopicIds] = useState<string[]>(
      entry.topicIds || [],
    )

    const people = entry.people || []
    const topics = entry.topics || []
    const dueInfo = formatDueDate(metadata.dueDate)
    const isDone = metadata.status === 'done'
    const isImportant =
      metadata.important ??
      (metadata.priority === 'urgent' || metadata.priority === 'high')

    const handleComplete = () => {
      if (isDone) return
      setJustCompleted(true)
      completeTask(entry.id, true)
    }

    // Cleanup timeout on unmount
    useEffect(() => {
      if (!justCompleted) return
      const timer = setTimeout(() => setJustCompleted(false), 600)
      return () => clearTimeout(timer)
    }, [justCompleted])

    const handleStartEdit = (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(true)
      setEditTitle(entry.title)
      setEditDescription(entry.content || '')
      setEditImportant(
        metadata.important ??
          (metadata.priority === 'urgent' || metadata.priority === 'high'),
      )
      setEditDueDate(metadata.dueDate || '')
      setEditPeopleIds(entry.people?.map((p) => p.id) || [])
      setEditTopicIds(entry.topicIds || [])
    }

    const handleCancelEdit = () => {
      setIsEditing(false)
    }

    const handleSave = async () => {
      await onSave({
        title: editTitle,
        description: editDescription || null,
        important: editImportant,
        dueDate: editDueDate || null,
        peopleIds: editPeopleIds,
        topicIds: editTopicIds,
      })
      setIsEditing(false)
    }

    // Handle checklist changes (always available, not just in edit mode)
    const handleChecklistChange = (items: ChecklistItem[]) => {
      const newItems = items.length > 0 ? items : null
      onSave({ checklist: newItems })
    }

    const selectedPeople = availablePeople.filter((p) =>
      editPeopleIds.includes(p.id),
    )

    // Inline meta editing handlers (view mode, no edit mode needed)
    const viewPeople = availablePeople.filter((p) =>
      people.some((ep) => ep.id === p.id),
    )

    const handleInlineDueDateChange = (date: Date | null) => {
      if (date) {
        onSave({ dueDate: formatDateString(date) })
      } else {
        onSave({ dueDate: null })
      }
    }

    const handleInlineTopicsChange = (topicIds: string[]) => {
      onSave({ topicIds })
    }

    const handleInlinePeopleAdd = (person: PersonRef) => {
      const currentIds = people.map((p) => p.id)
      onSave({ peopleIds: [...currentIds, person.id] })
    }

    const handleInlinePeopleRemove = (person: PersonRef) => {
      const currentIds = people
        .map((p) => p.id)
        .filter((id) => id !== person.id)
      onSave({ peopleIds: currentIds })
    }

    const header = (
      <div className="flex items-start gap-2 px-2 py-1.5 sm:py-2">
        {/* Checkbox — self-start + small top offset to align with title baseline */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleComplete()
          }}
          disabled={isDone}
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full p-0 transition-all duration-200 motion-reduce:transition-none',
            isDone
              ? 'text-success'
              : 'text-muted-foreground/60 hover:text-success hover:scale-110',
            justCompleted && 'animate-check-pop',
            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          )}
          aria-label={`Mark "${entry.title}" as complete`}
        >
          <CheckCircleIcon
            className={cn(
              'h-5 w-5 transition-all duration-200 motion-reduce:transition-none',
              justCompleted && 'text-success',
            )}
            weight={isDone || justCompleted ? 'fill' : 'regular'}
          />
        </button>

        {/* Expandable area */}
        <div
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onToggle()
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={
            isExpanded
              ? `Collapse task: ${entry.title}`
              : `Expand task: ${entry.title}`
          }
          aria-expanded={isExpanded}
          className="focus-visible:ring-ring flex min-w-0 flex-1 cursor-pointer flex-col rounded-sm text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          {/* Title row */}
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                'truncate text-sm',
                isDone && 'text-muted-foreground line-through',
              )}
            >
              {entry.title}
            </span>

            {isImportant && (
              <WarningCircleIcon
                className="text-destructive h-3.5 w-3.5 shrink-0"
                weight="fill"
              />
            )}

            {/* Metadata - right aligned */}
            <span className="ml-auto flex shrink-0 items-center gap-2">
              {checklistItems && checklistItems.length > 0 && (
                <TaskChecklistSummary items={checklistItems} />
              )}

              {/* Due date - hide in Today section AND calendar */}
              {dueInfo &&
                context !== 'upcoming-today' &&
                context !== 'calendar' &&
                (context === 'upcoming-other' ? (
                  <DateField
                    value={
                      metadata.dueDate
                        ? new Date(metadata.dueDate + 'T00:00:00')
                        : undefined
                    }
                    onChange={(newDate) => {
                      if (newDate) {
                        onSave({ dueDate: formatDateString(newDate) })
                      }
                    }}
                    variant="icon"
                    formatDate={() => dueInfo.text}
                    clearable={false}
                    className={cn(
                      'text-xs tabular-nums',
                      dueInfo.isOverdue
                        ? 'text-priority-urgent font-medium'
                        : 'text-muted-foreground',
                    )}
                  />
                ) : (
                  <span
                    className={cn(
                      'text-xs tabular-nums',
                      dueInfo.isOverdue
                        ? 'text-priority-urgent font-medium'
                        : 'text-muted-foreground',
                    )}
                  >
                    {dueInfo.text}
                  </span>
                ))}

              {/* Creation date - hide in Upcoming views AND calendar */}
              {!context?.startsWith('upcoming') && context !== 'calendar' && (
                <span className="text-muted-foreground/50 hidden text-xs sm:inline">
                  {formatRelativeTime(entry.createdAt)}
                </span>
              )}
            </span>
          </div>

          {/* Topics row — below title */}
          {!hideTopics && topics.length > 0 && (
            <div className="-mt-0.5">
              <TopicBadges topics={topics} maxDisplay={3} />
            </div>
          )}
        </div>
      </div>
    )

    return (
      <ExpandableRow
        isExpanded={isExpanded}
        isDone={isDone}
        entryType="task"
        header={header}
      >
        <div className="py-1 pr-2 pb-3 pl-9">
          {isEditing ? (
            <TaskEditForm
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              editDescription={editDescription}
              setEditDescription={setEditDescription}
              editImportant={editImportant}
              setEditImportant={setEditImportant}
              editDueDate={editDueDate}
              setEditDueDate={setEditDueDate}
              editPeopleIds={editPeopleIds}
              setEditPeopleIds={setEditPeopleIds}
              editTopicIds={editTopicIds}
              setEditTopicIds={setEditTopicIds}
              selectedPeople={selectedPeople}
              availablePeople={availablePeople}
              availableTopics={availableTopics}
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              onSave={handleSave}
            />
          ) : (
            // View mode: read-only title/description, but editable checklist + inline meta
            <>
              {entry.content && (
                <MarkdownContent className="font-body-serif text-foreground/80 mb-3 text-sm leading-relaxed">
                  {entry.content}
                </MarkdownContent>
              )}

              {/* Always editable checklist */}
              {checklistItems && checklistItems.length > 0 && (
                <div className={cn('mb-3', !entry.content && 'mt-2')}>
                  <EditableTaskChecklist
                    items={checklistItems}
                    onItemsChange={handleChecklistChange}
                    onToggle={(itemId, checked) =>
                      toggleChecklist(
                        entry.id,
                        toggleChecklistItem(checklistItems, itemId, checked),
                      )
                    }
                    disabled={isSaving}
                  />
                </div>
              )}

              {/* Inline meta tags row */}
              <MetaTagsRow className="mb-3">
                <DateField
                  value={
                    metadata.dueDate
                      ? new Date(metadata.dueDate + 'T00:00:00')
                      : undefined
                  }
                  onChange={handleInlineDueDateChange}
                  disabled={isSaving}
                  formatDate={(d) => format(d, 'MMM d')}
                  placeholder="+ due"
                  clearable={!!metadata.dueDate}
                  variant="inline"
                />

                <TopicSubtitleSelector
                  selectedTopicIds={entry.topicIds || []}
                  availableTopics={availableTopics}
                  onChange={handleInlineTopicsChange}
                  onAddNew={() => {}}
                  onCreateTopic={(name) => {
                    const topicId = findOrCreateTopic(name, 'task')
                    if (topicId) {
                      handleInlineTopicsChange([
                        ...(entry.topicIds || []),
                        topicId,
                      ])
                    }
                  }}
                  disabled={isSaving}
                />

                <MultiSelectProperty
                  label="People"
                  Icon={PROPERTY_ICONS.people}
                  selectedItems={viewPeople}
                  availableItems={availablePeople}
                  onAdd={handleInlinePeopleAdd}
                  onRemove={handleInlinePeopleRemove}
                  getItemLabel={(p) => p.name}
                  disabled={isSaving}
                />
              </MetaTagsRow>

              <div className="flex items-center gap-3">
                <SourceLink entry={entry} />
                <div className="flex-1" />
                <RowActions className="sm:-mr-2.5">
                  <EditButton
                    onClick={handleStartEdit}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                  <DeleteButton
                    onClick={onDelete}
                    isDeleting={isDeleting}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                </RowActions>
              </div>
            </>
          )}
        </div>
      </ExpandableRow>
    )
  },
  // Memoization comparison - only re-render if these props change
  (prevProps, nextProps) => {
    const prevMetadata = prevProps.entry.metadata as TaskMetadata
    const nextMetadata = nextProps.entry.metadata as TaskMetadata
    return (
      prevProps.entry.id === nextProps.entry.id &&
      prevProps.entry.title === nextProps.entry.title &&
      prevProps.entry.content === nextProps.entry.content &&
      prevMetadata.status === nextMetadata.status &&
      prevMetadata.dueDate === nextMetadata.dueDate &&
      prevMetadata.important === nextMetadata.important &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.isDeleting === nextProps.isDeleting &&
      prevProps.isSaving === nextProps.isSaving &&
      prevProps.context === nextProps.context &&
      JSON.stringify(prevMetadata.checklist) ===
        JSON.stringify(nextMetadata.checklist) &&
      JSON.stringify(prevProps.entry.topics) ===
        JSON.stringify(nextProps.entry.topics) &&
      JSON.stringify(prevProps.entry.people) ===
        JSON.stringify(nextProps.entry.people) &&
      prevProps.entry.url === nextProps.entry.url
    )
  },
)
